import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Text, useTheme } from '@Sage/hoi-poi-ui';

import ModalHoi from 'components/ModalHoi';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const INITIAL_OPTIONS = {
    copySender: true,
    copyRecipients: true,
    copySubject: true,
    copyDesign: false,
    copyAdditionalSettings: false,
};

const OPTION_CONFIG = [
    {
        key: 'copySender',
        titleLiteral: 'label_sender',
        descLiteral: 'label_marketing_campaigns_duplicate_campaign_sender_desc',
    },
    {
        key: 'copyRecipients',
        titleLiteral: 'label_recipients',
        descLiteral: 'label_marketing_campaigns_duplicate_campaign_recipients_desc',
    },
    {
        key: 'copySubject',
        titleLiteral: 'label_subject',
        descLiteral: 'label_marketing_campaigns_duplicate_campaign_subject_desc',
    },
    {
        key: 'copyDesign',
        titleLiteral: 'label_design',
        descLiteral: 'label_marketing_campaigns_duplicate_campaign_design_desc',
    },
    // {
    //     key: 'copyAdditionalSettings',
    //     titleLiteral: 'label_additional_settings',
    //     descLiteral: 'label_marketing_campaigns_duplicate_campaign_additional_settings_desc',
    // },
];

const DuplicateMarketingCampaignModal = memo(
    ({ isOpen, onRequestClose, onConfirm, isSubmitting }) => {
        const theme = useTheme();
        const [options, setOptions] = useState(INITIAL_OPTIONS);

        useEffect(() => {
            if (!isOpen) {
                return;
            }
            setOptions(INITIAL_OPTIONS);
        }, [isOpen]);

        const handleClose = useCallback(() => {
            onRequestClose?.();
        }, [onRequestClose]);

        const setOption = useCallback((key, checked) => {
            setOptions((prev) => ({ ...prev, [key]: checked }));
        }, []);

        const handleConfirm = useCallback(() => {
            onConfirm?.(options);
        }, [onConfirm, options]);

        return (
            <ModalHoi
                className="duplicate-marketing-campaign-modal"
                isOpen={isOpen}
                onRequestClose={handleClose}
                onCancel={handleClose}
                onConfirm={handleConfirm}
                title={getLiteral('title_marketing_campaigns_duplicate_confirm_modal')}
                cancelText={getLiteral('action_cancel')}
                confirmText={getLiteral('action_marketing_campaigns_duplicate_campaign')}
                isLoading={isSubmitting}
                size="medium"
                useHeader
                useCornerClose
                isConfirmDisabled={isSubmitting}
                buttonsLowercase
            >
                <Text className="duplicate-marketing-campaign-modal__intro" type="body">
                    {getLiteral('label_marketing_campaigns_duplicate_confirm_message')}
                </Text>
                {OPTION_CONFIG.map(({ key, titleLiteral, descLiteral }) => (
                    <div key={key} className="duplicate-marketing-campaign-modal__option">
                        <Checkbox
                            className="duplicate-marketing-campaign-modal__option-checkbox"
                            color="actionMinor"
                            size="medium"
                            checked={options[key]}
                            onChange={() => setOption(key, !options[key])}
                        />
                        <div className="duplicate-marketing-campaign-modal__option-text">
                            <Text medium>{getLiteral(titleLiteral)}</Text>
                            <Text type="caption" color={theme.colors.utility.textSecondary}>
                                {getLiteral(descLiteral)}
                            </Text>
                        </div>
                    </div>
                ))}
            </ModalHoi>
        );
    },
);

DuplicateMarketingCampaignModal.propTypes = {
    isOpen: PropTypes.bool,
    onRequestClose: PropTypes.func,
    onConfirm: PropTypes.func,
    isSubmitting: PropTypes.bool,
};

DuplicateMarketingCampaignModal.displayName = 'DuplicateMarketingCampaignModal';

export default DuplicateMarketingCampaignModal;
